import { PayloadAction } from '@reduxjs/toolkit';
import {
  AssessmentEvaluationActions,
  ConfigSaga,
  ConfigSelectors,
  ConfigTypes,
  DateUtils,
  FirebaseActionTypes,
  FirebaseActions,
  FirebaseTypes,
  HistogramActions,
  HistogramSelectors,
  IDomainCalculation,
  LanguageService,
  NotificationActions,
  ProgressActions,
  ProgressSelectors,
  RemoteConfigService,
  StatsActions,
  StatsSelectors,
  calculateDomainValues,
  getWorkoutId,
  logger,
  preloadAgeGroupSaga,
  preloadStatsSaga,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { WorkoutUnityResult } from 'web_unity_engine';
import {
  ASSESSMENT_SESSION_ID,
  CATEGORY_COMPONENT_ID,
  CATEGORY_STAT_TYPE,
  ColourblindnessCategoryGroupIds,
  DEFAULT_CATEGORY_ID,
  DyscalculiaCategoryGroupIds,
  SELF_ASSESSMENT_STAT_ID,
  TRAINING_LED,
  TRAINING_REMINDER,
} from '../base/constants';
import DomainCalculaionConfig from '../base/domain-calculation-config.json';
import ENV from '../base/env';
import { isOnPage, redirectInternal } from '../base/historyService';
import { EDomain, EExerciseCategory, IAssessmentConfigOPXP } from '../base/types';
import { CyclesActions, CyclesSelectors, CyclesTypes } from '../cycles';
import { ETrainingStatus } from '../cycles/types';
import { DashboardRoute } from '../dashboard/Dashboard';
import { ColourblindnessStatId, DyscalculiaStatId } from '../personalization/constants';
import { TrainingIntensityActions } from '../trainingIntensity';
import { UnityActions, UnitySelectors, UnityUtils } from '../unity';
import { TrainingLedRoute, TrainingScheduleRoute } from './AssessmentPage';
import * as Selectors from './selectors';
import { AssessmentActions } from './slice';
import { AssessmentError, EAssessmentSubpage, IOpenWorkoutPayload, TAssessmentWorkoutResult } from './types';
import * as Utils from './utils';

export function* preloadCycles() {
  const isCyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (isCyclesLoading) {
    yield take(CyclesActions.cyclesReady);
  }
}

export function* startAssessmentPageSaga() {
  const isStarted = yield select(Selectors.isAssessmentStarted);
  if (isStarted) {
    // page was started already - skip initialization
    return;
  }
  // load post-reg namespace
  yield call(LanguageService.loadNamespaces, 'postreg');

  // wait for cycles and stats ready action
  yield call(preloadCycles);
  yield call(preloadStatsSaga);
  yield call(waitForFirebaseReadySaga);

  // if current session is not assessment - redirect to dashboard
  const currentSessionId = yield select(CyclesSelectors.getCurrentSessionId);
  if (currentSessionId !== ASSESSMENT_SESSION_ID) {
    return redirectInternal(DashboardRoute);
  }
  yield call(assessmentNavigation);
}

export function* assessmentNavigation() {
  // check if we need to skip delay memory exercise due to timeout
  yield call(wsmDelayModeCheck);
  // check if assessment session was finished
  const assessmentFinished = yield select(CyclesSelectors.isAssessmentFinished);
  if (assessmentFinished) {
    yield call(postSessionNavigation);
    return;
  }
  // check if cfq session is ongoing
  const cfqToBeStarted = yield select(CyclesSelectors.cfqWorkoutToBeStartedInCurrentSession);
  if (cfqToBeStarted) {
    yield call(cfqNavigationSaga);
    return;
  }
  // assessment not finished
  // check if assessment session contains only from supported workouts
  const isCompatible = yield call(isAssessmentCompatible);
  if (!isCompatible) {
    yield call(rebuildAssessmentSession);
    // check if assessment session was finished after it was rebuilt
    const assessmentFinished = yield select(CyclesSelectors.isAssessmentFinished);
    if (assessmentFinished) {
      yield call(postSessionNavigation);
      return;
    }
  }
  // detect current exercise to run
  const nextWorkoutId: number = yield select(CyclesSelectors.getCurrentSessionFirstActiveWorkoutId);
  if (!nextWorkoutId) {
    throw new Error('Broken assessment session data!');
  }
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, nextWorkoutId);
  if (!exerciseId) {
    throw new Error('Assessment exercise was not defined!');
  }
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const assessmentRun = cycleId - 1; // first assessment must be in the cycleId = 2
  yield fork(startUnitySaga, nextWorkoutId, assessmentRun);
  const nextWorkoutIndex = yield select(CyclesSelectors.getActiveWorkoutRelativeIndex, nextWorkoutId);
  // set information about next workout to start
  yield put(AssessmentActions.openWorkout(nextWorkoutId, nextWorkoutIndex, exerciseId));
  // set subpage to session and feature state to ready
  yield put(AssessmentActions.assessmentStarted(EAssessmentSubpage.Session));
}

export function* getLastWorkoutTimestampFromProgress(workoutId: number) {
  // ensure workout progress is loaded
  yield call(waitForFullProgressSaga);
  // fetch timestamp from progress
  return yield select(ProgressSelectors.getLastWorkoutTimestamp, workoutId);
}

export function* wsmDelayModeCheck() {
  const workoutId: number | undefined = yield select(CyclesSelectors.getCurrentSessionFirstActiveWorkoutId);
  if (!workoutId) {
    // ignore cases when no active workout present
    return;
  }
  if (workoutId !== 1234) {
    // ignore cases when active workout is not the WSM delay mode
    return;
  }
  // get non-delay mode workout timestamp from progress
  const timestamp = yield call(getLastWorkoutTimestampFromProgress, 1134);
  const now = DateUtils.getUnixTimestamp();
  const deltaHr = (now - timestamp) / 60 / 60; // time gone in hours
  if (deltaHr < 3) {
    // do nothing if not much time gone
    return;
  }
  // skip workout if timeout reached
  const delayedIndex = yield select(CyclesSelectors.getActiveWorkoutRelativeIndex, 1234);
  if (delayedIndex !== -1) {
    yield put(CyclesActions.skipWorkout(1234, delayedIndex));
    yield take(CyclesActions.cycleSaved);
  }
}

export function* cfqNavigationSaga() {
  const workout: CyclesTypes.IWorkoutClientStatus | undefined = yield select(
    CyclesSelectors.getCurrentSessionFirstActiveWorkout
  );
  if (!workout) {
    throw new Error('Workout data is missing!');
  }
  // detect current workout
  const index = yield select(CyclesSelectors.getActiveWorkoutRelativeIndex, workout.workoutId);
  yield put(AssessmentActions.openCFQQuestion(workout.workoutId, index));
  // check if cfq was started already
  const cfqRelativeIndex = yield select(CyclesSelectors.getCFQWorkoutRelativeIndex, workout.workoutId);
  if (cfqRelativeIndex > 0) {
    yield put(AssessmentActions.startCFQ());
  }
  yield put(AssessmentActions.assessmentStarted(EAssessmentSubpage.CFQ));
}

export function* startUnitySaga(workoutId: number, assessmentRun: number) {
  // start unity and let it know that its an assessment
  yield put(UnityActions.initUnity('assessment', assessmentRun));
  const isStarted: boolean = yield select(UnitySelectors.isUnityStarted);
  // skip waiting if already started before
  if (!isStarted) {
    yield take(UnityActions.unityStarted);
  }
  // preload balancing file for given exercise
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  const exerciseJsonFile = yield select(ConfigSelectors.getExerciseJsonFile, workoutId);
  yield put(UnityActions.loadBalancing(exerciseId, exerciseJsonFile));
}

// this saga should navigate user to where he/she belong to if/after session was completed
export function* postSessionNavigation() {
  yield put(AssessmentActions.initEvaluationOverview());
  yield put(AssessmentActions.assessmentStarted(EAssessmentSubpage.Evaluation));
}

export function* loadWorkoutSaga(action: PayloadAction<IOpenWorkoutPayload>) {
  const { exerciseId, workoutId } = action.payload;
  const isUnityStarted = yield select(UnitySelectors.isUnityStarted);
  if (!isUnityStarted) {
    yield take(UnityActions.unityStarted);
  }
  yield put(UnityActions.prepareExercise(exerciseId, workoutId));
  yield take(UnityActions.exerciseReady);
  yield put(AssessmentActions.workoutReady());
}

export function* startWorkoutSaga() {
  const workoutId = yield select(Selectors.getWorkoutId);
  const duration = yield select(ConfigSelectors.getWorkoutDuration, workoutId);
  // default difficulty is 0, for WordSymbolMemory delayed mode its 3 (see BW-4666)
  const assessmentDifficulty = workoutId === 1234 ? 3 : 0;
  yield put(UnityActions.startExercise(assessmentDifficulty, duration));
}

export function* waitForWorkoutHistogram(workoutId: number) {
  let loaded = false;
  while (!loaded) {
    loaded = yield select(HistogramSelectors.isWorkoutHistogramLoaded, workoutId);
    if (!loaded) {
      yield delay(300);
    }
  }
}

export function* preloadWorkoutHistograms(workoutIds: number[]) {
  yield call(preloadStatsSaga);
  yield call(preloadAgeGroupSaga);
  const ageGroupId = yield select(StatsSelectors.getAgeGroup);
  // start loading histograms
  // if peer group has changed we will have a cache miss and new histograms will be loaded
  yield put(HistogramActions.loadHistogram(workoutIds, ageGroupId));
  // wait for histograms to be loaded
  const promiseBuffer: Array<Promise<unknown>> = [];
  for (const workoutId of workoutIds) {
    const task = yield fork(waitForWorkoutHistogram, workoutId);
    const promise: Promise<unknown> = task.toPromise();
    promiseBuffer.push(promise);
  }
  // wait until all workout histograms will be loaded
  yield Promise.all(promiseBuffer);
}

export function* calculatePerformaceByAssessment(assessmentResults: TAssessmentWorkoutResult[]) {
  const performanceByAssessment: Map<number, number> = new Map();
  for (const assessmentResult of assessmentResults) {
    const histogramPresent: boolean = yield select(
      HistogramSelectors.isWorkoutHistogramLoaded,
      assessmentResult.workoutId
    );
    let performance = 0;
    if (histogramPresent) {
      performance = yield select(
        HistogramSelectors.getResultNormalizedPerformanceForWorkout,
        assessmentResult.workoutId,
        assessmentResult.result
      );
    }
    if (performanceByAssessment.has(assessmentResult.workoutId)) {
      const maxPerformace = Math.max(performanceByAssessment.get(assessmentResult.workoutId) ?? 0, performance);
      performanceByAssessment.set(assessmentResult.workoutId, maxPerformace);
    } else {
      performanceByAssessment.set(assessmentResult.workoutId, performance);
    }
  }
  yield call(logger, ['performanceByAssessment', JSON.stringify(Array.from(performanceByAssessment.entries()))]);
  return performanceByAssessment;
}

export function* preloadCycleAssessment(cycleId: number) {
  yield call(preloadCycles);
  const assessment: CyclesTypes.ISessionClientStatus | undefined = yield select(
    CyclesSelectors.getCycleSession,
    cycleId,
    ASSESSMENT_SESSION_ID
  );
  if (!assessment) {
    // if assessment not present in state - try to load it from backend
    yield put(CyclesActions.loadAssessment(cycleId));
    yield take(CyclesActions.loadAssessmentComplete);
  }
}

export function* calculateAssessmentResult(cycleId: number) {
  yield call(preloadCycleAssessment, cycleId);
  const workoutIds: number[] | undefined = yield select(CyclesSelectors.getAssessmentWorkoutIds, cycleId);
  if (!workoutIds) {
    throw new AssessmentError(`Assessment for cycle ${cycleId} not found!`);
  }
  yield call(logger, ['getAssessmentWorkoutIds (from cycle):', workoutIds.join(',')]);
  const assessmentWorkouts: number[] = yield select(ConfigSelectors.filterAssessmentWorkoutIds, workoutIds);
  yield call(logger, ['assessmentWorkouts (filtered by config):', assessmentWorkouts.join(',')]);
  // preload progress data for the given cycle's assessment
  yield call(waitForFullProgressSaga);
  // Get assessment results
  const assessmentResults: TAssessmentWorkoutResult[] = yield select(
    ProgressSelectors.getLastProgressForEachWorkoutForCycleAndSession,
    assessmentWorkouts,
    ASSESSMENT_SESSION_ID,
    cycleId
  );
  yield call(logger, ['last assessment results:', assessmentResults]);
  // preload histograms
  yield call(preloadWorkoutHistograms, assessmentWorkouts);
  // Get domain weights
  const domainWeights: Map<EDomain, ConfigTypes.IWorkoutDomainWeight[]> = yield select(
    ConfigSelectors.getDomainWeights
  );
  // Calculate assesment normalized performance
  const performanceByAssessment: Map<number, number> = yield call(calculatePerformaceByAssessment, assessmentResults);
  // get timestamp of the finished assessment,
  // 0 means assessment was not finished yet
  // which might happen for currently finished assessment (data not refreshed)
  const timestamp: number = yield select(CyclesSelectors.getAssessmentFinishTimestamp, cycleId);
  // Finally calculate domain scores
  let domainScores: IDomainCalculation[] = yield call(
    calculateDomainValues,
    cycleId,
    performanceByAssessment,
    domainWeights,
    timestamp
  );
  yield call(logger, ['domain scores: ', domainScores]);
  const selfAssessmentWorkouts: number[] = yield select(ConfigSelectors.filterSelfassessmentWorkoutIds, workoutIds);
  if (selfAssessmentWorkouts.length > 0) {
    // Get assessment results
    const selfAssessmentResults: TAssessmentWorkoutResult[] = yield select(
      ProgressSelectors.getLastProgressForEachWorkoutForCycleAndSession,
      selfAssessmentWorkouts,
      ASSESSMENT_SESSION_ID,
      cycleId
    );
    yield call(logger, ['selfAssessmentResults:', selfAssessmentResults]);
    const cognitiveFailureWeights: Map<EDomain, ConfigTypes.IWorkoutDomainWeight[]> = yield select(
      ConfigSelectors.getCognitiveFaluresWeights
    );
    yield call(logger, ['cognitiveFailureWeights:', cognitiveFailureWeights.entries()]);
    const resultByQuestion: Map<number, number> = new Map();
    for (const assessmentQuestionResult of selfAssessmentResults) {
      resultByQuestion.set(assessmentQuestionResult.workoutId, assessmentQuestionResult.result);
    }
    const cfqScores = yield call(Utils.calculateCFQDomainScores, cycleId, resultByQuestion, cognitiveFailureWeights);
    yield call(logger, ['self assessment domain results:', cfqScores]);
    // merge arrays
    domainScores = domainScores.concat(cfqScores);
  }
  return domainScores;
}

export function* calculateDomainPreferences(domainScores: IDomainCalculation[]) {
  const filteredDomains: IDomainCalculation[] = yield call(Utils.removeCFQDomains, domainScores);
  yield call(waitForFirebaseReadySaga);
  const domainCalculationConfig: typeof DomainCalculaionConfig = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigDomainCalculation,
    DomainCalculaionConfig
  );
  const normalizedValues: Map<number, number> = yield call(
    Utils.calculateDomainNormalizedComplementoryValues,
    filteredDomains,
    domainCalculationConfig
  );
  return normalizedValues;
}

export function* handleInitEvaluationOverview() {
  yield call(preloadCycles);
  const cycleId: number = yield select(CyclesSelectors.getCurrentCycleId);
  const domainScores: IDomainCalculation[] = yield call(calculateAndSaveCycleEvaluation, cycleId);
  // calculate domain preferences
  const preferences: Map<number, number> = yield call(calculateDomainPreferences, domainScores);
  // save calculated preference to user stats
  for (const [domainId, preferencePercentage] of preferences.entries()) {
    const statId = `9-5-${domainId}`;
    // preferred domains are saved in scale 0..1M to make space for user selection
    const valueInt = Math.round(preferencePercentage * 1000000);
    yield put(StatsActions.updateGenericStat(statId, { valueInt }));
  }
  if (Utils.cfqDomainIsPresent(domainScores)) {
    // save CFQ assessment timestamp only during evaluation to avoid marking cfq as done if it was skipped
    yield put(StatsActions.updateStringStat(SELF_ASSESSMENT_STAT_ID, DateUtils.getUnixTimestamp().toString()));
  }
  yield put(StatsActions.saveStatsQueue());
  const sortedDomainCalculations = yield call(Utils.getLastEvaluation, domainScores);
  yield put(AssessmentActions.storeEvaluationResults(sortedDomainCalculations));
  yield put(TrainingIntensityActions.evaluationLoaded());
}

export function* calculateAndSaveCycleEvaluation(cycleId: number) {
  // make domain calculations
  const domainScores: IDomainCalculation[] = yield call(calculateAssessmentResult, cycleId);
  // save domain calculations to backend
  yield put(AssessmentEvaluationActions.saveCycleEvaluationAction({ cycleId, domaincalculation: domainScores }));
  // wait for calculations being saved
  yield take(AssessmentEvaluationActions.cycleEvaluationSaved);
  return domainScores;
}

export function* completeWorkoutSaga(action: PayloadAction<WorkoutUnityResult>) {
  const result = action.payload;
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  if (sessionId !== ASSESSMENT_SESSION_ID) {
    //return if we are not doing assessment
    return;
  }
  if (UnityUtils.isWorkoutCancelled(result)) {
    // switch back to intro screen
    yield put(AssessmentActions.restartWorkout());
    return;
  }
  const workoutId = yield select(Selectors.getWorkoutId);
  if (workoutId === 1134 && UnityUtils.isWorkoutFailed(result)) {
    // memory task failed - skip delayed mode
    const delayedIndex = yield select(CyclesSelectors.getActiveWorkoutRelativeIndex, 1234);
    if (delayedIndex !== -1) {
      yield put(CyclesActions.skipWorkout(1234, delayedIndex));
      yield take(CyclesActions.cycleSaved);
    }
  }
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const variant = yield select(ConfigSelectors.getConfigVariant);
  const version = yield select(ConfigSelectors.getConfigVersion);
  // save progress
  yield put(ProgressActions.saveProgress(variant, version, cycleId, ASSESSMENT_SESSION_ID, workoutId, result));
  // TODO: probably we should start and wait for progress saving to be completed (BW-7361)
  // to make sure we have a consistent state on backend
  // update cycle
  const workoutIndex = yield select(Selectors.getWorkoutIndex);
  yield put(CyclesActions.completeWorkout(workoutId, workoutIndex));
  yield take(CyclesActions.cycleSaved);
  yield call(assessmentNavigation);
}

export function* handleRestartWorkout() {
  const exerciseId = yield select(Selectors.getExerciseId);
  const workoutId = yield select(Selectors.getWorkoutId);
  yield put(UnityActions.prepareExercise(exerciseId, workoutId));
  yield take(UnityActions.exerciseReady);
  yield put(AssessmentActions.workoutReady());
}

export function* isAssessmentCompatible() {
  yield call(waitForFirebaseReadySaga);
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const assessmentConfig: IAssessmentConfigOPXP = yield call(Utils.getAssessmentConfigForCycle, cycleId);
  const assessmentSteps = assessmentConfig.assessment_ids[ASSESSMENT_SESSION_ID];
  yield call(ConfigSaga.preloadConfig);
  const sessionLength: number = yield select(CyclesSelectors.getCurrentSessionLengthWithoutCFQ);
  const startIndex = sessionLength > assessmentSteps.length ? sessionLength - assessmentSteps.length : 0;
  for (let index = startIndex; index < sessionLength; index++) {
    const workout: CyclesTypes.IWorkoutClientStatus = yield select(
      CyclesSelectors.getCurrentSessionWorkoutByRelativeIndex,
      index
    );
    const isSupported = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workout.workoutId);
    if (!isSupported && workout.status !== ETrainingStatus.Skipped) {
      // If unsupported workout that is not skipped, it's not compatible
      return false;
    }
    const isCompleted = yield select(CyclesSelectors.isCurrentSessionWorkoutByRelativeIndexFinished, index);
    if (isCompleted) {
      // skip workout if it was completed already
      continue;
    }
    const isAssessmentWorkout = yield select(ConfigSelectors.isAssessmentWorkout, workout.workoutId);
    if (!isAssessmentWorkout) {
      return false;
    }
    // we are checking for workout to be present in current config
    // if user has outdated assessment session we won't be able to show proper locas for it
    const isWorkoutInCurrentConfig = yield call(
      Utils.workoutIsInConfig,
      workout.workoutId,
      ASSESSMENT_SESSION_ID,
      assessmentConfig
    );
    if (!isWorkoutInCurrentConfig) {
      return false;
    }
  }
  return true;
}

export function* rebuildAssessmentSession() {
  // ensure firebase is loaded
  yield call(waitForFirebaseReadySaga);
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const assessmentConfig: IAssessmentConfigOPXP = yield call(Utils.getAssessmentConfigForCycle, cycleId);
  yield put(CyclesActions.rebuildAssessment(assessmentConfig));
  yield take(CyclesActions.assessmentCreated);
}

export function* answerReceivedSaga(action: PayloadAction<number>) {
  const answer = action.payload;
  // save score to progress
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const workoutId = yield select(Selectors.getWorkoutId);
  const variant = yield select(ConfigSelectors.getConfigVariant);
  const version = yield select(ConfigSelectors.getConfigVersion);
  yield put(ProgressActions.saveProgressScore(variant, version, cycleId, ASSESSMENT_SESSION_ID, workoutId, answer));
  // TODO: consider if needed: start saving and wait for progress save end (BW-7361)
  // update cycle data
  const workoutIndex = yield select(Selectors.getWorkoutIndex);
  // complete current workout
  yield put(CyclesActions.completeWorkout(workoutId, workoutIndex));
  // activate next workout if possible
  const relativeIndex = yield select(CyclesSelectors.getNonSkippedWorkoutRelativeIndex, workoutId);
  const nextIndex = relativeIndex + 1;
  const sessionLength = yield select(CyclesSelectors.getCurrentSessionLength);
  if (nextIndex >= sessionLength) {
    // trigger evaluation loading screen
    yield put(AssessmentActions.assessmentStarted(EAssessmentSubpage.CFQEvaluation));
  }
  yield all([
    delay(2000), // wait for two seconds
    take(CyclesActions.cycleSaved), // and also for cycle save completion
  ]);
  // detect next view
  yield call(assessmentNavigation);
}

export function* restartUnitySaga() {
  const isAssessmentPage = isOnPage('assessment');

  if (!isAssessmentPage) {
    return;
  }

  const workoutId = yield select(Selectors.getWorkoutId);
  const workoutIndex = yield select(Selectors.getWorkoutIndex);
  const exerciseId = yield select(Selectors.getExerciseId);

  yield put(AssessmentActions.openWorkout(workoutId, workoutIndex, exerciseId));
  yield take(AssessmentActions.workoutReady);

  const isRunning = yield select(Selectors.isRunning);

  if (!isRunning) {
    return;
  }

  yield put(AssessmentActions.startWorkout());
}

export function* cycleEvaluationSaga(action: PayloadAction<number>) {
  const cycleId = action.payload;
  yield call(calculateAndSaveCycleEvaluation, cycleId);
  yield put(AssessmentActions.cycleEvaluationComplete());
}

export function* redirectToSchedule() {
  yield call(redirectInternal, TrainingScheduleRoute);
}

export function* handleScheduleSelection(action: PayloadAction<number>) {
  const itemId = action.payload;
  yield put(FirebaseActions.requestPushPermissionAction(ENV.VapId));
  const permissionResult: FirebaseActionTypes.IPermissionResultAction = yield take(
    FirebaseActionTypes.PUSH_PERMISSION_RESULT
  );
  const permissionGranted = permissionResult.permission ? 1 : 0;
  // send firebase action
  const trainingScheduleSelectPayload: FirebaseTypes.IEventActionPayload = {
    eventName: 'o_training_reminder',
    parameters: [
      {
        key: 'Permission',
        value: permissionGranted,
      },
      {
        key: 'Time',
        value: `${itemId}:00`,
      },
    ],
  };
  yield put(FirebaseActions.analyticsEventAction(trainingScheduleSelectPayload));

  const t = LanguageService.getTFunction();

  yield put(
    StatsActions.updateGenericStat(TRAINING_REMINDER, {
      valueInt: permissionGranted,
      valueTxt: `${itemId}:00`,
    })
  );
  yield put(StatsActions.saveStatsQueue());
  const saveResultActions = [StatsActions.saveStatsQueueFailed, StatsActions.saveStatsQueueSuccess];
  const result = yield take(saveResultActions);
  if (result.type === StatsActions.saveStatsQueueFailed.type) {
    yield put(NotificationActions.showError(t('common.common:Errors.System')));
    yield put(AssessmentActions.trainingScheduleSaveFailed());
    return;
  }

  // switch to next subpage
  yield put(AssessmentActions.trainingScheduleSelected(permissionGranted)); // feature/BW-1666 hide LED step

  // send training data to backend, skipping LED step
  // yield put(Actions.trainingLedUpdateAction(null, permissionGranted)); // null means that we skip LED step
}

export function* handlePeerGroupSelection(action: PayloadAction<number>) {
  const groupId = action.payload;
  const peerGroupSelectPayload: FirebaseTypes.IEventActionPayload = {
    eventName: 'o_age_selection',
    parameters: [
      {
        key: 'Age_group',
        value: groupId,
      },
    ],
  };
  yield put(FirebaseActions.analyticsEventAction(peerGroupSelectPayload));

  yield all([
    // wait until all data are saved and loaded
    call(handleAgeGroupSave, groupId),
    // or at least 1 second
    delay(1000),
  ]);
}

export function* handleAgeGroupSave(groupId: number) {
  // wait until progress saving process ends
  // this should not be needed as we are waiting for end
  // explicitly after each exercise now
  const isProgressSaving = yield select(ProgressSelectors.isProgressSaving);
  if (isProgressSaving) {
    yield take(ProgressActions.endSaveProgress);
  }
  // save age group
  yield put(StatsActions.saveAgeGroup(groupId));
  // wait for request completed
  yield take(StatsActions.loadPeerGroups);
  // start calculating evaluation results
  yield put(AssessmentActions.initEvaluationOverview());
}

export function* handleSpecialNeeds(action: PayloadAction<number>) {
  const itemId = action.payload;
  if (!itemId) {
    // nothing needs to be adjusted if standard mode is chosen
    yield call(redirectInternal, TrainingLedRoute);
    return;
  }
  if (itemId === 1 || itemId === 3) {
    // Colourblindness is chosen
    yield call(saveColourblindnessSaga);
  }
  if (itemId === 2 || itemId === 3) {
    // Dyscalculia is chosen
    yield call(saveDyscalculiaSaga);
  }
  yield put(StatsActions.saveStatsQueue());
  yield call(redirectInternal, TrainingLedRoute);
}

export function* saveColourblindnessSaga() {
  for (const categoryGroupId of ColourblindnessCategoryGroupIds) {
    const workoutId = yield call(getWorkoutIdFromCategoryGroupId, categoryGroupId);
    const workoutStatId = Utils.parseWorkoutPreferenceStatId(workoutId);
    // valueInt === 1 means that workout is disabled
    yield put(StatsActions.updateGenericStat(workoutStatId, { valueInt: 1 }));
  }
  // save disability stat
  yield put(StatsActions.updateGenericStat(ColourblindnessStatId, { valueInt: 1 }));
}

export function* saveDyscalculiaSaga() {
  for (const categoryGroupId of DyscalculiaCategoryGroupIds) {
    const workoutId = yield call(getWorkoutIdFromCategoryGroupId, categoryGroupId);
    const workoutStatId = Utils.parseWorkoutPreferenceStatId(workoutId);
    // valueInt === 1 means that workout is disabled
    yield put(StatsActions.updateGenericStat(workoutStatId, { valueInt: 1 }));
  }
  // save disability stat
  yield put(StatsActions.updateGenericStat(DyscalculiaStatId, { valueInt: 1 }));
}

export function* getWorkoutIdFromCategoryGroupId(categoryGroupId: number) {
  const categoryIdStat: EExerciseCategory | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    `${CATEGORY_STAT_TYPE}-${categoryGroupId}-${CATEGORY_COMPONENT_ID}`
  );
  // if stat is not set use default category (normal difficulty)
  const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
  return getWorkoutId(categoryId, categoryGroupId);
}

export function* activateTrainingLedSaga() {
  const trainingLedActivatePayload: FirebaseTypes.IEventActionPayload = {
    eventName: 'o_training_led',
    parameters: [
      {
        key: 'Led',
        value: 'true',
      },
    ],
  };
  yield put(FirebaseActions.analyticsEventAction(trainingLedActivatePayload));
}

export function* deactivateTrainingLedSaga() {
  const trainingLedDeactivatePayload: FirebaseTypes.IEventActionPayload = {
    eventName: 'o_training_led',
    parameters: [
      {
        key: 'Led',
        value: 'false',
      },
    ],
  };
  yield put(FirebaseActions.analyticsEventAction(trainingLedDeactivatePayload));
}

export function* handleTrainingLedUpdate(action: PayloadAction<0 | 1 | null>) {
  const trainingLed = action.payload;
  yield call(waitForFirebaseReadySaga);

  // save post-evaluation stats
  const t = LanguageService.getTFunction();

  if (trainingLed !== null) {
    yield put(StatsActions.updateNumericStat(TRAINING_LED, trainingLed));
  }
  yield put(StatsActions.saveStatsQueue());
  const saveResultActions = [StatsActions.saveStatsQueueFailed, StatsActions.saveStatsQueueSuccess];
  const result = yield take(saveResultActions);
  if (result.type === StatsActions.saveStatsQueueFailed.type) {
    yield put(NotificationActions.showError(t('common.common:Errors.System')));
    yield put(AssessmentActions.trainingLedSaveFailed());
    return;
  }
  yield put(AssessmentActions.trainingLedSaved());
}

export default function* assessmentPageWatcher() {
  yield all([
    takeEvery(AssessmentActions.initAssessment, startAssessmentPageSaga),
    takeEvery(AssessmentActions.openWorkout, loadWorkoutSaga),
    takeEvery(AssessmentActions.startWorkout, startWorkoutSaga),
    takeEvery(UnityActions.exerciseComplete, completeWorkoutSaga),
    takeEvery(AssessmentActions.initEvaluationOverview, handleInitEvaluationOverview),
    takeEvery(AssessmentActions.restartWorkout, handleRestartWorkout),
    takeEvery(UnityActions.unityRestarted, restartUnitySaga),
    takeLatest(AssessmentActions.answerCFQ, answerReceivedSaga),
    takeEvery(AssessmentActions.cycleEvaluation, cycleEvaluationSaga),
    takeLatest(AssessmentActions.createTrainingPlanClick, redirectToSchedule),
    takeEvery(AssessmentActions.selectTrainingSchedule, handleScheduleSelection),
    takeEvery(AssessmentActions.ageGroupSelected, handlePeerGroupSelection),
    takeLeading(AssessmentActions.selectSpecialNeeds, handleSpecialNeeds),
    takeLeading(AssessmentActions.trainingLedActivate, activateTrainingLedSaga),
    takeLeading(AssessmentActions.trainingLedDeactivate, deactivateTrainingLedSaga),
    takeEvery(AssessmentActions.trainingLedUpdate, handleTrainingLedUpdate),
  ]);
}
